import { useEffect, useState } from "react";
import { useGetOrganizationDetail } from "../api/organization";
import { useParams } from "react-router-dom";
import { Badge } from "antd";
import { User } from "lucide-react";
import { Icons } from "../assets/images/icons";
import { InfoCard } from "../components/cards/info-card";

function BranchDetailPage() {
  const { id } = useParams();
  const { data: branch, refetch } = useGetOrganizationDetail(id);
  interface Address {
    region?: string;
    city?: string;
    kifleKetema?: string;
    woreda?: string;
    house_number?: string;
  }

  const [address, setAddress] = useState<Address | null>(null);
  interface User {
    fullName: string;
    phoneNo: string;
    email: string;
  }

  const [contactPerson, setContactPerson] = useState<User | null>(null);
  const [numberOfUsers, setNumberOfUsers] = useState(0);
  const {
    name,
    type,
    email,
    status,
    phoneNo,
    address: addressData,
    createdBy,
    createdAt,
    updatedBy,
    updatedAt,
  } = branch?.data?.data || {};

  useEffect(() => {
    if (id) refetch();
  }, [id]);

  useEffect(() => {
    if (branch?.data?.data) {
      const { address: addressData, user } = branch.data.data;
      if (addressData) setAddress(JSON.parse(addressData));
      if (user?.length) {
        setNumberOfUsers(user.length);
        setContactPerson(user.find((u: User) => u));
      }
    }
  }, [branch]);

  const parsedAddress = (() => {
    try {
      return addressData ? JSON.parse(addressData) : null;
    } catch {
      return null;
    }
  })();

  const branchInfo = [
    { title: "Branch Name", value: name },
    { title: "Type", value: type },
    { title: "Status", value: status },
    { title: "Phone", value: phoneNo },
    { title: "Email", value: email },
    { title: "No. of Users", value: numberOfUsers },
  ];

  const addressInfo = parsedAddress
    ? [
        { title: "Region", value: parsedAddress.region },
        { title: "City", value: parsedAddress.city },
        { title: "Sub-City", value: parsedAddress.subCity || "N/A" },
        { title: "Woreda", value: parsedAddress.woreda },
        { title: "House Number", value: parsedAddress.houseNumber || "" },
      ]
    : [];

  const branchSection = [...branchInfo, ...addressInfo];

  const contactInfo = [
    { title: "Full Name", value: contactPerson?.fullName },
    { title: "Email", value: contactPerson?.email },
    { title: "Phone Number", value: contactPerson?.phoneNo },
  ];

  const metaData = [
    {
      title: "Created By",
      value: createdBy,
      icon: User,
    },
    {
      title: "Created At",
      value: createdAt,
      icon: Icons.calendar03Icon,
    },
    {
      title: "Updated By",
      value: updatedBy || "N/A",
      icon: User,
    },
    {
      title: "Updated At",
      value: updatedAt || "N/A",
      icon: Icons.arrowReloadIcon,
    },
  ];

  return (
    <div
      className="bg-white px-4 md:px-8 py-8 h-screen"
      style={{ fontFamily: "Poppins" }}
    >
      {/* Branch Info */}
      <div className="bg-white border border-[#E6EEF6] rounded-2xl p-6 w-full mt-3">
        <h2 className="text-base font-medium text-black mb-6">
          Branch Information
        </h2>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-y-4 gap-x-6 mb-4">
          {branchSection.map((branch, index) => (
            <InfoCard title={branch.title} value={branch.value} key={index} />
          ))}
        </div>
      </div>

      {/* Contact Person */}
      <div className="bg-white border border-[#E6EEF6] rounded-2xl p-6 w-full mt-3">
        <h2 className="text-base font-medium text-black mb-6">
          Contact Person
        </h2>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-y-4 gap-x-6 mb-4">
          {contactInfo.map((contact, index) => (
            <InfoCard
              title={contact.title}
              value={contact.value as any}
              key={index}
            />
          ))}
        </div>
      </div>

      {/* Meta Data */}
      <div className="bg-white border border-[#E6EEF6] rounded-2xl p-6 w-full mt-3">
        <h2 className="text-base font-medium text-black mb-6">
          System Information
        </h2>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-y-4 gap-x-6 mb-4">
          {metaData.map((meta, index) => (
            <InfoCard
              title={meta.title}
              value={meta.value}
              icon={meta?.icon}
              key={index}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

interface DetailItemProps {
  label: string;
  value: string | number | null | undefined;
  boldLabel?: boolean;
}

const DetailItem = ({ label, value }: DetailItemProps) => (
  <div className="flex justify-between items-center border-b py-2">
    <span className="text-gray-700 font-medium">{label}:</span>
    <Badge className="text-gray-800">{value || "N/A"}</Badge>
  </div>
);

export default BranchDetailPage;
